import type { ComponentType } from 'react';

import { CustomAttributeFileUpload } from '../renderers/field-layouts/CustomAttributeFileUpload';
import type { CustomAttributeProps } from '../renderers/field-layouts/CustomAttributeProps';
import { type FieldTypeConfig } from './types';

/**
 * "Attach file" custom field. Files are NOT stored in `CustomAttributeData` —
 * they live in the field-scoped CUS-78 junction (`relation_custom_attribute_file`)
 * and are synced after save by `useCustomAttributeFileSave`, exactly like the
 * built-in Files field. The field is therefore excluded from every reporting /
 * tabular surface (see `excludeFromReporting`).
 */
export const attachFile: FieldTypeConfig = {
  i18nKey: 'customAttributes.fieldTypes.attachfile',
  FieldComponent:
    CustomAttributeFileUpload as unknown as ComponentType<CustomAttributeProps>,
  // Never consumed: `excludeFromReporting` (below) removes attach-file from
  // every tabular surface before `getTableFieldConfig` would be called. Throw
  // rather than return empty data, so a regression that bypasses the exclusion
  // fails loud instead of silently rendering blank cells / exports.
  getTableFieldConfig: () => {
    throw new Error(
      'attach-file fields are excluded from reporting (excludeFromReporting); getTableFieldConfig must not be called'
    );
  },
  // Offered only when ALL of these flags are on: `trpc` (the save path —
  // presign + customAttributeFiles.sync — is tRPC-only) and the dedicated
  // `attach_file_custom_field` rollout gate (so it can be exposed per-env
  // independently). Drop `'trpc'` here once every customer is on trpc.
  requiresFeatureFlags: ['trpc', 'attach_file_custom_field'],
  // Files are a field-scoped relation (CUS-78 junction), not tabular data —
  // exclude from register columns, custom datasources, filters and widgets,
  // exactly like the built-in Files field.
  excludeFromReporting: true,
};
